/**
 * Garde des espaces `/admin` et `/scan` : session staff obligatoire, avec un
 * rôle attendu (ARCHITECTURE §9.1).
 *
 * ⚠️ Ce n’est qu’un confort d’interface. **La sécurité est côté serveur**
 * (préhandlers `requireRole`) : masquer un écran n’a jamais protégé une route.
 */

import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'

import { peutOuvrirEcran, type RoleUtilisateur } from '@tb/shared'

import { CHEMINS, urlConnexion } from '../lib/chemins'
import { ChargementPage } from '../ui/Spinner'
import { AccesRefuse } from './AccesRefuse'
import { aAuMoinsRole, aRole } from './roles'
import { useAuth } from './useAuth'

export interface RequireStaffProps {
  /** Rôle minimal, au sens hiérarchique (`admin` ⊇ `guichet` ⊇ `lecture`). */
  readonly roleMinimal?: RoleUtilisateur | undefined
  /** Liste explicite — l’emporte sur `roleMinimal` quand elle est fournie. */
  readonly roles?: readonly RoleUtilisateur[] | undefined
  /** Sans enfant, la garde rend l'`<Outlet>` des routes filles. */
  readonly children?: ReactNode | undefined
}

export function RequireStaff({ roleMinimal, roles, children }: RequireStaffProps) {
  const { session, chargement } = useAuth()
  const emplacement = useLocation()

  if (chargement) return <ChargementPage libelle="Vérification de la session…" />

  if (session === null || session.sujet !== 'staff') {
    const destination = `${emplacement.pathname}${emplacement.search}`
    return <Navigate to={urlConnexion(CHEMINS.connexionStaff, destination)} replace />
  }

  const role = session.utilisateur.role
  const autorise =
    roles !== undefined
      ? aRole(role, ...roles)
      : roleMinimal === undefined || aAuMoinsRole(role, roleMinimal)

  if (!autorise || !peutOuvrirEcran(session.utilisateur, emplacement.pathname)) {
    return <AccesRefuse rolesAttendus={roles ?? (roleMinimal === undefined ? [] : [roleMinimal])} />
  }

  return <>{children ?? <Outlet />}</>
}
